<template>
  <div id="app" class="body1 layout-container flex justify-center">
    <transition name="fade">
      <div class="splash-screen" v-if="splashScreen">
        <div class="wrap"><img src="/static/images/logo.svg" id="logo" alt="logo" /></div>
      </div>
    </transition>

    <the-vertical-nav
      :collapse-nav="collapseNav"
      :nav-view.sync="navView"
      :disabled="disableNav"
      :isUnread="unreadNotification"
      @collapse-nav-toggle="collapseNav = !collapseNav"
      @notification-toggle="setNotificationToggle()"
      v-if="!$_.isEmpty(isAuthenticated)"
    />

    <div class="container flex column box grow" :class="{ 'nav-collapsed': collapseNav }">
      <div class="main box grow flex"><router-view /></div>
    </div>

    <notifications group="session_notifications">
      <template slot="body" slot-scope="props">
        <notification-card :notification="props.item.data" :show-action="false"></notification-card>
      </template>
    </notifications>

    <notifications />

    <aside-sidebar
      id="notifications-feed"
      parent=".container"
      position="left"
      :feed-loading="loadNotifications"
      :show-close="false"
      v-model="hideNotifications"
      ref="notificationSidebar"
    >
      <div slot="title" class="box flex gaps">
        <div class="box cursor-pointer" @click="closeNotificationSidebar()">
          <i class="material-icons">arrow_back</i>
        </div>
        <div class="box">
          <h2 class="mv-0">
            Notifications
            <el-link @click="loadDocumentation('page-notifications')" :underline="false">
              <i class="material-icons">help_outline</i>
            </el-link>
          </h2>
        </div>
        <!-- <div class="box right self-flex-end">
          <i class="cursor-pointer nav-icon c-icon-settings" @click="gotoProfile()"></i>
        </div>-->
      </div>
      <div slot="content">
        <div
          v-if="notificationListLoaded"
          v-infinite-scroll="load"
          infinite-scroll-disabled="disabled"
          infinite-scroll-distance="10"
          style="overflow:auto;"
          id="notificationvlist"
          :key="notificationvlistKey"
        >
          <div v-for="(val, idx) in range(0, notificationCount)" :key="idx">
            <notification-card
              :notification="notificationModels[val]"
              @notification-read="readNotification(notificationModels[val], idx)"
              @notification-clear="clearNotification(notificationModels[val], idx)"
              @notification-toggle="setNotificationToggle()"
            ></notification-card>
          </div>
        </div>
      </div>
    </aside-sidebar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { registerInverval, destroyInverval } from '@/utilities/global.interval'
import ApiRest from '@/api/api-rest'
import { AUTH_TOKEN_REFRESH, AUTH_TOKEN_REFRESH_INTERVAL } from '@/store/modules/auth/auth.type'
import { AGENT_LIST_REQ } from '@/store/modules/agents/agents.type'
import { NOTIFICATION_LIST_REQUEST, ALERT_LIST_REQUEST } from '@/store/modules/notifications/notifications.type'
import { USER_PERMISSION_SET } from '@/store/modules/user/user.type'
import { INSIGHTS_ENABLED } from '@/store/modules/feature_enabled/feature_enabled.type'
import { IQHUB_VERSION_REQ, IQ_PORTAL_VERSION_REQ } from '@/store/modules/version/version.type'
import TheVerticalNav from '@/components/layout/TheVerticalNav.vue'
import Toolbar from '@/components/layout/Toolbar.vue'
import AsideSidebar from '@/components/layout/AsideSidebar.vue'
import NotificationCard from '@/components/card/notification/NotificationCard.vue'
import { NOTIFICATIONS_POLLING_INTERVAL } from '@/constants/polling-interval.constants'
import loadDocumentationMixin from '@/mixins/loadDocumentationMixin'

export default {
  name: 'App',
  mixins: [loadDocumentationMixin],
  data () {
    return {
      collapseNav: true, // collapsed on login
      disableNav: false,
      navView: '',
      splashScreen: false,
      hideNotifications: true,
      loadNotifications: false,
      notificationCardHeight: 172,
      notificationVsize: 5,
      alertsIntervalId: {},
      notificationCount: 0,
      notificationListLoaded: false,
      notificationvlistKey: 0, // key used to re-render notifications list
      notificationModels: [],
      unreadNotification: false
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'notificationsReqStatus', 'notificationList', 'userRoles', 'userPermissions', 'activeAgentList']),
    disabled () {
      return this.notificationList.models ? this.notificationCount >= this.notificationList.models.length : true
    },
    agentNameIdMap() {
      let nameIdMap = new Map();
      for (let agent of this.activeAgentList.models) {
        nameIdMap.set(agent.agentDetails.agentId, agent.agentDetails.hostname);
      }
      return nameIdMap;
    }
  },
  created () {
    // only called on hard refresh (user is authenticated)
    // do not refresh on logout
    if (!_.isEmpty(this.isAuthenticated) && this.$route.name !== 'logout') {
      this.$store.dispatch(AGENT_LIST_REQ) // update assets badge count

      // re-establish RBAc Permissions
      let permissions = _.flatten(_.map(this.userRoles, 'permissions'))
      this.$store.dispatch(USER_PERMISSION_SET, permissions)
      this.$store.dispatch(INSIGHTS_ENABLED)
      this.$store.dispatch(IQHUB_VERSION_REQ)
      this.$store.dispatch(IQ_PORTAL_VERSION_REQ)

      // create API instance
      ApiRest()

      // during hard refresh, make immediate call to refresh token
      this.$store.dispatch(AUTH_TOKEN_REFRESH).then(
        token => {
          // schedule periodical call to refresh the token
          this.$store.dispatch(AUTH_TOKEN_REFRESH_INTERVAL)
        },
        errMsg => {
          console.log(errMsg)
        }
      )
    }
  },
  mounted () {
    // window resize event
    let debounceResize = _.debounce(this.forceRerenderList, 500)

    window.addEventListener('resize', debounceResize)
  },
  methods: {
    checkUnreadNotification() {
      for (let notification of this.notificationList.models) {
        if (!notification.read) {
          return true
        }
      }
      return false
    },
    resetNotificationListData () {
      this.notificationModels = []
      this.notificationCount = 0
      this.notificationListLoaded = true
    },
    forceRerenderList () {
      setTimeout(() => {
        this.resetNotificationListData()
        this.notificationvlistKey += 1
        this.setListHeight()
      })
    },
    setListHeight () {
      setTimeout(() => {
        const listElement = document.getElementById('notificationvlist')
        const listHeight = this.$parent.$el.clientHeight - 77
        if (!_.isEmpty(listElement)) {
          listElement.style.height = `${listHeight}px`
        }
      })
    },
    range (start, end) {
      let rangeArr = []
      for (let i = start; i < end; i++) {
        rangeArr.push(i)
      }
      return rangeArr
    },
    load () {
      const notification = this.notificationList.models[this.notificationCount]
      if (notification.type === 'HOST' && notification.event_action === 'SEVERITY_CHANGE'){
        notification.details.hostname = this.agentNameIdMap.get(notification.event_resource_id)
      }
      this.notificationModels.push(notification)
      this.notificationCount += 1
    },
    closeNotificationSidebar () {
      this.$refs.notificationSidebar.close()
    },
    setNotificationToggle () {
      this.hideNotifications = !this.hideNotifications
      if (!this.hideNotifications) {
        this.$store.dispatch(NOTIFICATION_LIST_REQUEST).then(resp => {
          this.loadNotifications = false
          this.forceRerenderList()
        })
      }
    },
    gotoProfile () {
      this.setNotificationToggle()
      this.$router.push({
        name: 'account',
        query: { view: 'email' }
      })
    },
    popupNotification (notification) {
      this.$alert({
        group: 'session_notifications',
        width: '400',
        duration: 3000,
        data: notification
      })
      this.unreadNotification= true
    },
    clearNotification (notification, index) {
      this.notificationList.models.splice(index, 1)
      this.notificationModels.splice(index, 1)
      this.notificationCount -= 1
    },
    readNotification (notification, index) {
      notification.read = true
      this.unreadNotification = this.checkUnreadNotification()
    },
    getAlertsInterval () {
      this.alertsIntervalId = setTimeout(() => {
        this.getAlerts()
      }, NOTIFICATIONS_POLLING_INTERVAL)

      registerInverval(this.alertsIntervalId)
    },
    getAlerts () {
      if (this.alertsIntervalId) {
        destroyInverval(this.alertsIntervalId)
      }
      let last10Secs = new Date(Date.now() - 10000).toISOString()
      this.$store.dispatch(ALERT_LIST_REQUEST, { createdOn: last10Secs }).then(
        details => {
          if (!_.isEmpty(details)) {
            for (let idx = 0; idx < details.length; idx++) {
              setTimeout(() => {
                this.popupNotification(details[idx])
              }, 200)
            }
          }
          this.getAlertsInterval()
        },
        errMsg => {
          console.log(errMsg)
          this.getAlertsInterval()
        }
      )
    }
  },
  watch: {
    isAuthenticated: {
      handler: function (newAuth, oldAuth) {
        if (!_.isEmpty(newAuth) && _.isEmpty(this.alertsIntervalId)) {
          this.getAlertsInterval();
          this.$store.dispatch(NOTIFICATION_LIST_REQUEST).then(resp => {
            this.unreadNotification = this.checkUnreadNotification()
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  beforeDestroy () {
    // use empty function to kill interval update calls (timing insurance)
    this.getAlertsInterval = function () {}
    this.getAlertsInterval()
    destroyInverval(this.alertsIntervalId)
  },
  components: {
    TheVerticalNav,
    Toolbar,
    AsideSidebar,
    NotificationCard
  }
}
</script>

<style>
/* Global CSS to bootstrap project from Foresttheme Vue Pragmatic
  TODO: need to remove unnessecary styles as this is the stylesheet built for prod
*/
@import 'assets/styles/pragmatic.css';
@import 'element-ui/lib/theme-chalk/index.css';
@import 'assets/styles/confluera.css';
@import 'assets/styles/text.css';
@import 'assets/styles/global_classes.css';
@import 'assets/styles/_variables.css';
@import 'assets/styles/riskscore_colors.css';
@import 'assets/styles/colors.css';
@import 'assets/styles/icon.css';
@import 'assets/styles/logos.css';

html:not(.ie) .layout-container .container {
  max-width: none;
}

#app {
  font-family: 'Muli', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100%;
  color: var(--gray-800);
  background-color: var(--background-color);
}

#logo {
  height: 38px;
  width: 34px;
}

.layout-container .container {
  transition: width 0.5s cubic-bezier(0.2, 0, 0, 1) 0s;

  .header {
    margin: 0px;
    padding: 30px 20px 10px;
    background-color: white;

    h1 {
      font-weight: 400;
    }
  }

  .main {
    padding: 0px;
  }

  .view {
    padding: 10px 25px;
    overflow: hidden;
  }

  .page-table {
    height: 100%;
  }
}

.el-card {
  border: 1px solid var(--border-color);
}

.nav-icon {
  display: inline-block;
  margin: auto;
  background: var(--aluminum-500);
}

.c-icon-reporting {
  width: 24px;
  height: 24px;
  mask: url('/static/images/Report_clipboard.svg');
  mask-size: cover;
  mask-repeat: no-repeat;
}

.c-icon-settings {
  width: 24px;
  height: 24px;
  mask: url('/static/images/settings.svg');
  mask-size: cover;
  mask-repeat: no-repeat;
}

.vue-notification-group {
  min-width: 400px;
}

#notifications-feed .el-link.el-link--default {
  color: #c6cbd2;

  .material-icons {
    font-size: 20px;
  }
}

.el-dialog__body {
  word-break: break-word;
}

textarea {
  font-family: 'Muli', sans-serif;
}

a {
  color: var(--denim-600);
  text-decoration: none;
}
</style>



// WEBPACK FOOTER //
// src/App.vue